import React from 'react';
import Select from 'react-select';

/**
 * Option interface for multi-select items
 * @property value - The value to be used when option is selected
 * @property label - The text to display for the option
 */
interface Option {
  value: string;
  label: string;
}

/**
 * Props for the MultiSelect component
 * @property id - Unique identifier for the select
 * @property name - Form field name
 * @property label - Optional label text above select
 * @property value - Array of currently selected values
 * @property onChange - Callback function when selection changes
 * @property options - Array of available options
 * @property placeholder - Placeholder text when no option is selected
 * @property className - Additional CSS classes
 * @property required - Whether the field is required
 * @property disabled - Whether the select is disabled
 * @property showAsterisk - Whether to show the required asterisk
 */
interface MultiSelectProps {
  id: string;
  name: string;
  label?: string;
  value?: Option[];
  onChange: (value: Option[]) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  showAsterisk?: boolean;
}

/**
 * MultiSelect Component
 * 
 * A reusable multi-select component that provides:
 * - Multiple selection support
 * - Custom styling with Tailwind CSS
 * - Dark mode support
 * - Searchable options
 * - Label with optional required indicator
 * - Disabled state support
 * - Customizable styles
 * - Responsive design
 */
const MultiSelect: React.FC<MultiSelectProps> = ({
  id,
  name,
  label,
  value = [],
  onChange,
  options,
  placeholder = 'Select options',
  className = '',
  required = false,
  disabled = false,
  showAsterisk = false,
}) => {
  const portalTarget = typeof document !== 'undefined' ? document.body : undefined;
  return (
    <div className="mb-4">
      {/* Label with optional required indicator */}
      {label && (
        <label 
          htmlFor={id} 
          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
        >
          {label}
          {showAsterisk && required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="mt-2">
        {/* React-Select component with custom styling */}
        <Select
          id={id}
          name={name}
          isMulti
          menuPortalTarget={portalTarget}
          menuPosition="fixed"
          value={value}
          onChange={(newValue) => onChange(newValue as Option[])}
          options={options}
          placeholder={placeholder}
          isDisabled={disabled}
          className={`w-full ${className}`}
          classNamePrefix="multiselect"
          theme={(theme) => ({
            ...theme,
            borderRadius: 8,
            colors: {
              ...theme.colors,
              // Primary colors for focus and selection states
              primary: '#3b82f6',
              primary75: '#60a5fa',
              primary50: '#93c5fd',
              primary25: '#bfdbfe',
              // Neutral colors for light mode
              neutral0: 'rgb(249 250 251)', // Background
              neutral5: 'rgb(243 244 246)', // Hover state
              neutral10: 'rgb(229 231 235)', // Active state
              neutral20: '#6b7280', // Border
              neutral30: '#9ca3af', // Disabled
              neutral40: '#d1d5db', // Placeholder
              neutral50: '#e5e7eb',
              neutral60: '#f3f4f6',
              neutral70: '#f9fafb',
              neutral80: '#ffffff',
              neutral90: '#ffffff',
            },
          })}
          styles={{
            // Main control container
            control: (base, state) => ({
              ...base,
              minHeight: '44px',
              padding: '2px 4px',
              backgroundColor: 'rgb(249 250 251)',
              borderColor: 'rgb(209 213 219)',
              borderRadius: '0.5rem',
              boxShadow: 'none',
              '&:hover': {
                borderColor: 'rgb(156 163 175)',
              },
              '&:focus-within': {
                borderColor: '#3b82f6',
                boxShadow: '0 0 0 1px #3b82f6',
              },
              // Dark mode styles
              '.dark &': {
                backgroundColor: '#1e293b',
                borderColor: 'rgb(55 65 81)',
              }
            }),

            // Container for selected values and input
            valueContainer: (base) => ({
              ...base,
              padding: '2px 8px',
            }),

            // Input field
            input: (base) => ({
              ...base,
              color: 'rgb(17 24 39)', // Dark text for light mode
              margin: '2px',
              padding: '2px',
              '.dark &': {
                color: 'white', // White text for dark mode
              }
            }),

            // Render dropdown menu in portal with high z-index
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),

            // Dropdown menu
            menu: (base) => ({
              ...base,
              backgroundColor: 'rgb(249 250 251)', // Light mode default
              border: '1px solid rgb(209 213 219)',
              borderRadius: '0.5rem',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              marginTop: '4px',
              '.dark &': {
                backgroundColor: '#374151', // Changed to match CustomDropdown
                border: 'none',
              }
            }),

            // Menu list container
            menuList: (base) => ({
              ...base,
              padding: '4px',
            }),

            // Individual option in dropdown
            option: (base, state) => ({
              ...base,
              padding: '10px 14px',
              fontSize: '0.8rem',
              // Light mode colors
              backgroundColor: state.isSelected 
                ? 'rgb(229 231 235)'
                : state.isFocused 
                  ? 'rgb(229 231 235)'
                  : 'transparent',
              color: 'rgb(17 24 39)',
              '&:hover': {
                backgroundColor: 'rgb(229 231 235)',
              },
              borderRadius: '0.375rem',
              // Dark mode colors
              '.dark &': {
                backgroundColor: state.isSelected 
                  ? '#4b5563'
                  : state.isFocused 
                    ? '#4b5563'
                    : 'transparent',
                color: state.isSelected || state.isFocused
                  ? 'white'
                  : 'rgb(229 231 235)',
                '&:hover': {
                  backgroundColor: '#4b5563',
                  color: 'white',
                },
              }
            }),

            // Selected value tag
            multiValue: (base) => ({
              ...base,
              backgroundColor: '#4b5563',
              borderRadius: '0.375rem',
              margin: '2px',
            }),

            // Selected value tag text
            multiValueLabel: (base) => ({
              ...base,
              color: 'white',
              padding: '4px 8px',
              fontSize: '0.75rem',
            }),

            // Remove button for selected value
            multiValueRemove: (base) => ({
              ...base,
              color: 'white',
              padding: '4px',
              '&:hover': {
                backgroundColor: '#374151',
                color: 'white',
              },
              borderRadius: '0 0.375rem 0.375rem 0',
            }),

            // Placeholder text
            placeholder: (base) => ({
              ...base,
              color: 'rgb(156 163 175)',
              fontSize: '0.875rem',
            }),

            // Selected value text
            singleValue: (base) => ({
              ...base,
              color: 'white',
            }),

            // Clear all button
            clearIndicator: (base) => ({
              ...base,
              padding: '4px',
            }),

            // Dropdown indicator
            dropdownIndicator: (base) => ({
              ...base,
              padding: '4px',
            }),
          }}
        />
      </div>
    </div>
  );
};

export default MultiSelect; 